import { createContext, useState, useEffect } from 'react';
import { sources, findSourceByLandingURL } from '../data/sources';
import { useLocation } from 'react-router-dom';
import { trackPersonProperties } from './posthogUtils';

export const SourceContext = createContext();

export const SourceProvider = ({ children }) => {
    const location = useLocation();

    const [source, setSource] = useState(null);
    const [safeSource, setSafeSource] = useState(null);
    const [switchAndSave, setSwitchAndSave] = useState(null);
    const [utmMedium, setUtmMedium] = useState(null);
    const [utmCampaign, setUtmCampaign] = useState(null);
    const [utmContent, setUtmContent] = useState(null);
    const [utmTerm, setUtmTerm] = useState(null);
    const [mcTransactionId, setMcTransactionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const currentDateTime = new Date().toISOString();        

    useEffect(() => {
        const today = currentDateTime.split('T')[0];
        localStorage.setItem('todaysDate', today);

        const subdomain = window.location.host.split('.')[0];
        localStorage.setItem('subdomain', subdomain);

        const urlParams = new URLSearchParams(window.location.search);
        let utmSource = urlParams.get('utm_source') ? urlParams.get('utm_source').toLowerCase() : '';
        let mcTransactionIdValue = urlParams.get('sub2') ? urlParams.get('sub2').toLowerCase() : '';

        // Fall back to path segment (e.g. /benefithub) if no UTM param — SourceRedirect hasn't fired yet
        if (!utmSource) {
            const pathParts = window.location.pathname.split('/');
            const sourceSegment = pathParts.length > 1 ? pathParts[1].toLowerCase() : '';
            const pathSource = findSourceByLandingURL(sourceSegment);
            if (pathSource) utmSource = pathSource.utmSource;
        }

        // Check if there are any query parameters and store them in localStorage for potential future use
        const storedParams = JSON.parse(localStorage.getItem('allQueryParams') || '{}');
        if (!storedParams || Object.keys(storedParams).length === 0) {
            const allParams = {};
            urlParams.forEach((value, key) => {
                allParams[key] = value;
            });
            localStorage.setItem('allQueryParams', JSON.stringify(allParams));
        }

        // Save utm_medium, utm_campaign, utm_content and utm_term for potential future use
        const utm_medium = urlParams.get('utm_medium') ? urlParams.get('utm_medium').toLowerCase() : '';
        const utm_campaign = urlParams.get('utm_campaign') ? urlParams.get('utm_campaign').toLowerCase() : '';
        const utm_content = urlParams.get('utm_content') ? urlParams.get('utm_content').toLowerCase() : '';
        const utm_term = urlParams.get('utm_term') ? urlParams.get('utm_term').toLowerCase() : '';

        if (utm_medium) {
            localStorage.setItem('utm_medium', utm_medium);
            setUtmMedium(utm_medium);
        }
        if (utm_campaign) {
            localStorage.setItem('utm_campaign', utm_campaign);
            setUtmCampaign(utm_campaign);
        }
        if (utm_content) {
            localStorage.setItem('utm_content', utm_content);
            setUtmContent(utm_content);
        }
        if (utm_term) {
            localStorage.setItem('utm_term', utm_term);
            setUtmTerm(utm_term);
        }

        // Check if utmSource or subdomain is a wholesale source or a retail source, with priority to utmSource

        let sourceValue = '';

        // Priority to utm_source
        if (utmSource) {
            sourceValue = utmSource;
            localStorage.setItem('source', sourceValue);
            localStorage.setItem('mcTransactionId', mcTransactionIdValue);
        } else if (sources[subdomain]?.wholesale || sources[subdomain]?.retail) {
            sourceValue = subdomain;
            localStorage.setItem('source', sourceValue);
            localStorage.setItem('mcTransactionId', mcTransactionIdValue);
        } else {
            // Check if previous source exists and is not older than 7 days
            const storedDateTime = localStorage.getItem('sourceSetDateTime');
            let sourceExpired = true;
            
            if (storedDateTime) {
                const storedDate = new Date(storedDateTime);
                const daysDifference = (new Date(currentDateTime) - storedDate) / (1000 * 60 * 60 * 24);
                sourceExpired = daysDifference > 7;
            }        

            if (localStorage.getItem('source') && !sourceExpired) {
                sourceValue = localStorage.getItem('source');
                mcTransactionIdValue = localStorage.getItem('mcTransactionId');
            } else {
                sourceValue = 'tb2'; // default source
                localStorage.setItem('source', sourceValue);
                localStorage.setItem('mcTransactionId', mcTransactionIdValue);
            }
        }
        
        if (sourceValue === 'podcast') {
            localStorage.setItem('discount', sourceValue);
        }

        // Check if source is safe
        if (sources[sourceValue]?.safe) {
            setSafeSource(true);
        } else {
            setSafeSource(false);
        }

        // Check if source is switchAndSave
        if (sources[sourceValue]?.switchAndSave) {
            setSwitchAndSave(true);
        } else {
            setSwitchAndSave(false);
        }

        setSource(sourceValue);
        setMcTransactionId(mcTransactionIdValue);
        localStorage.setItem('sourceSetDateTime', currentDateTime);
        setLoading(false);

        // Track initial UTM source person property in PostHog (as a set once property)
        if (sourceValue) trackPersonProperties({}, { '$initial_utm_source': sourceValue });

    }, [location.search]);

    if (loading) {
        return null;
    }

    return (
        <SourceContext.Provider value={{ source, utmMedium, utmCampaign, utmContent, utmTerm, safeSource, switchAndSave }}>
            {children}
        </SourceContext.Provider>
    );
};